import { TrashAltIcon } from "assets/icons";
import { If } from "components";
import { useDebouncedValue } from "hooks";
import { AllowedReseller } from "modules/common";
import { useGetResellers } from "modules/resellers";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import { SearchInput } from "ui";

import styles from "./AllowedResellersField.module.scss";

const SEARCH_DEBOUNCE_MS = 300;
// Small on purpose: the list flows inside the modal's scroll area, so a long
// one pushes the buttons far off-screen. Narrowing the query beats scrolling.
const SEARCH_RESULTS_LIMIT = 6;

interface AllowedResellersFieldProps {
  /** The zone's current allowlist, used to label ids the search has not seen. */
  initialSelected: AllowedReseller[];
  value: string[];
  onChange: (resellerIds: string[]) => void;
}

/**
 * Picks the users who keep seeing a hidden zone.
 *
 * The form only carries ids, but chips need names — so this keeps a lookup of
 * everyone it has ever seen, seeded from the zone and extended from each search.
 * Without it, an already-allowlisted user would render as a bare uuid until they
 * happened to turn up in a search result.
 */
const AllowedResellersField = ({
  initialSelected,
  value,
  onChange
}: AllowedResellersFieldProps) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const [known, setKnown] = useState<Record<string, AllowedReseller>>(() =>
    Object.fromEntries(initialSelected.map((reseller) => [reseller.id, reseller]))
  );

  const { resellers, isLoading } = useGetResellers(
    { page: 1, take: SEARCH_RESULTS_LIMIT, search: debouncedSearch },
    { enabled: debouncedSearch.trim().length > 0 }
  );

  const results = useMemo(
    () => (resellers ?? []).filter((reseller) => !value.includes(reseller.id)),
    [resellers, value]
  );

  const hasQuery = Boolean(debouncedSearch.trim());

  const add = (reseller: AllowedReseller) => {
    setKnown((previous) => ({ ...previous, [reseller.id]: reseller }));
    onChange([...value, reseller.id]);
    setSearch("");
  };

  const remove = (resellerId: string) => {
    onChange(value.filter((id) => id !== resellerId));
  };

  return (
    <div className={styles.allowed}>
      <SearchInput
        value={search}
        placeholder={t("Search users by name, phone or email")}
        onChange={setSearch}
      />

      <If condition={hasQuery && isLoading}>
        <div className={styles.allowed_loader}>
          <ClipLoader size={16} color="#fb3973" />
        </div>
      </If>

      <If condition={hasQuery && !isLoading}>
        <ul className={styles.allowed_results}>
          {results.map((reseller) => (
            <li key={reseller.id}>
              <button
                type="button"
                className={styles.allowed_result}
                onClick={() =>
                  add({
                    id: reseller.id,
                    name: [reseller.firstname, reseller.lastname]
                      .filter(Boolean)
                      .join(" "),
                    phoneNumber: reseller.phoneNumber,
                    email: reseller.email
                  })
                }
              >
                <span className={styles.allowed_name}>
                  {reseller.firstname} {reseller.lastname}
                </span>
                <span className={styles.allowed_meta}>
                  {reseller.phoneNumber}
                  {reseller.email ? ` · ${reseller.email}` : ""}
                </span>
              </button>
            </li>
          ))}

          <If condition={results.length === 0}>
            <li className={styles.allowed_empty}>{t("Nothing found")}</li>
          </If>
        </ul>
      </If>

      <If condition={value.length === 0}>
        <p className={styles.allowed_empty}>
          {t("Nobody yet — the zone will be reachable from the admin panel only.")}
        </p>
      </If>

      <ul className={styles.allowed_selected}>
        {value.map((resellerId) => {
          const reseller = known[resellerId];

          return (
            <li key={resellerId} className={styles.allowed_chip}>
              <span className={styles.allowed_name}>
                {reseller?.name ?? t("Unknown user")}
              </span>
              <span className={styles.allowed_meta}>
                {reseller?.phoneNumber}
                {reseller?.email ? ` · ${reseller.email}` : ""}
              </span>
              <button
                type="button"
                className={styles.allowed_remove}
                onClick={() => remove(resellerId)}
              >
                <TrashAltIcon width={14} height={14} fill="#ff0033" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AllowedResellersField;
